import { Injectable } from '@angular/core';
import { GridColumnComponent } from '../components/grid/grid-column/grid-column.component';
import { downloadBlob } from '../utils/file-utils';

@Injectable({ providedIn: 'root' })
export class CsvBuilder {
  // #6738: Prevent CSV Formula Injection
  static escapeUnsafe(value: any) {
    return typeof value === 'string' ? value.replace(/^([=+\-@])/, "'$1") : (value ?? '');
  }

  readonly UTF8_BOM = new Uint8Array([0xef, 0xbb, 0xbf]);

  constructor() {}

  build<T extends {}>(
    rowData: readonly T[],
    columns: ReadonlyArray<GridColumnComponent<T, any>>,
    options: {
      fileName: string;
      prepend?: string[];
      processCellCallback?(params: { colId: string; data: T; value: any }): string;
    },
  ) {
    const fileParts: BlobPart[] = [this.UTF8_BOM];

    if (options?.prepend) {
      for (const line of options.prepend) {
        fileParts.push(this.toCellValue(line) + '\n');
      }
    }

    if (columns.some(c => !!c.group?.headerName)) {
      fileParts.push(
        columns
          .map(c => c.group?.headerName ?? '')
          .map(this.toCellValue)
          .join(',') + '\n',
      );
    }

    fileParts.push(
      columns
        .map(c => c.headerName)
        .map(this.toCellValue)
        .join(',') + '\n',
    );

    rowData.forEach(row => {
      fileParts.push(
        columns
          .map(c => {
            const value = c.valueGetter(row);
            return options?.processCellCallback
              ? options.processCellCallback({ colId: c.field as string, data: row, value })
              : value;
          })
          .map(this.toCellValue)
          .join(',') + '\n',
      );
    });

    if (fileParts.length > 1) {
      fileParts[fileParts.length - 1] = (fileParts[fileParts.length - 1] as string).replace(
        /\n$/,
        '',
      );
    }

    const blob = new Blob(fileParts, { type: 'text/csv' });

    downloadBlob(blob, options.fileName ?? 'Export', { appendExtension: true });
  }

  private toCellValue(value: any) {
    const escaped = CsvBuilder.escapeUnsafe(value).toString();
    if (!escaped) {
      return '""';
    }
    return `"${escaped.replace('"', '""')}"`;
  }
}
